<script setup lang="ts">
import { useI18n } from '@/i18n';
import { SUPPORTED_CURRENCIES } from '@/lib/currencies';
import { useBillingStore } from '@/stores/billing';
import { Dropdown, Icon, Menu, MenuHeading, MenuItem, vTooltip } from '@dabble/quillui';
import { computed } from 'vue';

const { t } = useI18n();
const billingStore = useBillingStore();

const NAMES: Record<string, string> = {
  usd: 'US Dollar',
  eur: 'Euro',
  gbp: 'British Pound',
  cad: 'Canadian Dollar',
  aud: 'Australian Dollar',
  nzd: 'New Zealand Dollar',
};

// Offer only the currencies the plans are actually priced in (from the offerings
// response); fall back to the full supported list until that field is available.
const currencies = computed(() => {
  const offered = billingStore.offerings?.currencies?.map((c) => c.toLowerCase());
  const codes = offered?.length ? offered : [...SUPPORTED_CURRENCIES];
  const set = new Set(codes);
  const ordered = SUPPORTED_CURRENCIES.filter((c) => set.has(c));
  const extras = codes.filter((c) => !ordered.includes(c as (typeof SUPPORTED_CURRENCIES)[number]));
  return [...ordered, ...extras].map((code) => ({ code, name: NAMES[code] ?? code.toUpperCase() }));
});

const current = computed(() => (billingStore.currency ?? 'usd').toLowerCase());

// Legacy/USD-only plans can't be billed in another currency, so switching would
// dead-end any later plan/interval change (DAB-849). Show the control, disabled,
// with a tooltip explaining why rather than hiding it.
const canSwitch = computed(() => billingStore.canSwitchCurrency);

function choose(code: string) {
  if (code === current.value) return;
  void billingStore.setCurrency(code);
}
</script>

<template>
  <span
    v-if="!canSwitch"
    v-tooltip="t('checkout_currency_locked_plan')"
    class="currency-switcher__pill currency-switcher__pill--disabled"
    :aria-label="t('checkout_currency_locked_plan')"
  >
    {{ current.toUpperCase() }}
  </span>
  <Dropdown v-else placement="bottom">
    <template #trigger="{ onClick }">
      <button
        type="button"
        class="currency-switcher__pill"
        :aria-label="t('checkout_currency_change')"
        @click="onClick"
      >
        {{ current.toUpperCase() }}
        <Icon name="chevron-down" size="xsmall" class="currency-switcher__chevron" />
      </button>
    </template>
    <Menu>
      <MenuHeading borderless>{{ t('checkout_currency_label') }}</MenuHeading>
      <MenuItem
        v-for="c in currencies"
        :key="c.code"
        :selected="c.code === current"
        :icon-right="c.code === current ? 'check' : undefined"
        @click="choose(c.code)"
      >
        <span class="currency-switcher__item">
          <span class="currency-switcher__item-code">{{ c.code.toUpperCase() }}</span>
          <span class="currency-switcher__item-name">{{ c.name }}</span>
        </span>
      </MenuItem>
    </Menu>
  </Dropdown>
</template>

<style lang="scss" scoped>
.currency-switcher__pill {
  display: inline-flex;
  align-items: center;
  gap: space('xtiny');
  padding: space('xtiny') space('xsmall');
  background-color: theme('color-fg-app');
  border: 1px solid theme('color-border-primary');
  border-radius: 9999px;
  color: theme('color-text-secondary');
  font: inherit;
  font-size: fontSize('xsmall');
  font-weight: fontWeight('semibold');
  letter-spacing: 0.06em;
  line-height: 1;
  cursor: pointer;
  transition:
    color 120ms ease,
    background-color 120ms ease,
    border-color 120ms ease;

  &:hover {
    color: theme('color-text-primary');
    border-color: theme('color-border-interactive');
  }

  &:focus-visible {
    outline: 2px solid theme('color-fg-primary-interactive');
    outline-offset: 2px;
  }
}

.currency-switcher__chevron {
  margin-right: -0.1em;
  opacity: 0.7;
}

.currency-switcher__pill--disabled {
  cursor: default;
  opacity: 0.55;

  &:hover {
    color: theme('color-text-secondary');
    border-color: theme('color-border-primary');
  }
}

.currency-switcher__item {
  display: flex;
  align-items: baseline;
  gap: space('small');
}

.currency-switcher__item-code {
  min-width: 2.75rem;
  font-weight: fontWeight('semibold');
}

.currency-switcher__item-name {
  color: theme('color-text-secondary');
  font-size: fontSize('small');
}
</style>
